import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { needsReauth, REAUTH_VALID_MS } from '@domain/policies/SessionSecurityPolicy';
import { getServices } from '@infrastructure/services';

/**
 * El permiso dice quién puede; esto comprueba que sigue siendo él.
 *
 * Antes de enseñar el expediente o el sueldo **de otra persona** se vuelve a
 * pasar por Google. No es un segundo factor nuevo: las cuentas entran con Google
 * y Workspace ya exige verificación en dos pasos, así que esto es volver a pasar
 * el que ya hay — lo que añade no es un factor, es **recencia**. Una sesión
 * abierta prueba que alguien se identificó alguna vez; esto prueba que el dueño
 * está delante ahora.
 *
 * **Vale diez minutos**, para que revisar cinco fichas seguidas no sean cinco
 * ventanas. Y vive en memoria a propósito: recargar la página la vuelve a pedir,
 * que es lo correcto para un permiso que no debería sobrevivir a nada.
 *
 * Lo que **no** pasa por aquí es el expediente propio. Nadie tiene que demostrar
 * que es él para mirarse a sí mismo, y pedirlo enseñaría que el portal no
 * distingue entre consultar y consultarse.
 */

interface SensitiveAccess {
  /** True si ya está autorizado ahora mismo, para pintar sin parpadeo. */
  readonly granted: boolean;
  /**
   * Pide la reidentificación si hace falta. Devuelve si se puede enseñar.
   *
   * Una sola ventana a la vez: dos tarjetas sensibles en la misma pantalla
   * llamarían dos veces y Google abriría dos ventanas, de las cuales una fallaría
   * sola y dejaría el mensaje de que no se pudo verificar sobre una verificación
   * que sí ocurrió.
   */
  readonly ensure: () => Promise<boolean>;
}

const SensitiveAccessContext = createContext<SensitiveAccess | null>(null);

export const SensitiveAccessProvider = ({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode => {
  const [lastReauth, setLastReauth] = useState<number | null>(null);
  const enCurso = useRef<Promise<boolean> | null>(null);

  const ensure = useCallback(async (): Promise<boolean> => {
    if (!needsReauth(lastReauth, Date.now())) return true;
    if (enCurso.current !== null) return enCurso.current;

    const intento = getServices()
      .auth.reauthenticate()
      .then((ok) => {
        if (ok) setLastReauth(Date.now());
        return ok;
      })
      .finally(() => {
        enCurso.current = null;
      });

    enCurso.current = intento;
    return intento;
  }, [lastReauth]);

  const value = useMemo(
    (): SensitiveAccess => ({
      granted: !needsReauth(lastReauth, Date.now()),
      ensure,
    }),
    [lastReauth, ensure],
  );

  return (
    <SensitiveAccessContext.Provider value={value}>{children}</SensitiveAccessContext.Provider>
  );
};

/**
 * Fuera del proveedor concede, para que una prueba o una vista previa no reviente.
 * No es un agujero: la pantalla que lo usa ya está detrás de su permiso.
 */
export const useSensitiveAccess = (): SensitiveAccess =>
  useContext(SensitiveAccessContext) ?? { granted: true, ensure: () => Promise.resolve(true) };

export { REAUTH_VALID_MS };
